/**
 * Central registry for tool categories.
 *
 * To add a new category: add one entry below. `ToolCategoryId` and
 * `toolCategoryLabels` are derived from this array — nothing else to edit.
 */

export type IconKey =
  | 'wallet'
  | 'heart-pulse'
  | 'graduation-cap'
  | 'calendar-check'
  | 'sparkles'
  | 'globe'
  | 'file-text'
  | 'dice-5';

export type AccentKey =
  | 'emerald'
  | 'rose'
  | 'violet'
  | 'sky'
  | 'cyan'
  | 'amber'
  | 'slate'
  | 'orange';

export interface ToolCategoryDef {
  id: string;
  label: string;
  description: string;
  icon: IconKey;
  accent: AccentKey;
  order: number;
}

export const toolCategories = [
  {
    id: 'finance',
    label: 'Finance',
    description: 'Budgeting, loans, investing, and everyday money math.',
    icon: 'wallet',
    accent: 'emerald',
    order: 1,
  },
  {
    id: 'health',
    label: 'Health',
    description: 'Sleep, fitness, and wellness calculators.',
    icon: 'heart-pulse',
    accent: 'rose',
    order: 2,
  },
  {
    id: 'study',
    label: 'Study',
    description: 'Grades, focus timers, and writing helpers for students.',
    icon: 'graduation-cap',
    accent: 'violet',
    order: 3,
  },
  {
    id: 'daily',
    label: 'Daily',
    description: 'Small everyday conversions and date math.',
    icon: 'calendar-check',
    accent: 'sky',
    order: 4,
  },
  {
    id: 'ambient',
    label: 'Ambient',
    description: 'Color, focus screens, and other calm little tools.',
    icon: 'sparkles',
    accent: 'cyan',
    order: 5,
  },
  {
    id: 'web',
    label: 'Web',
    description: 'Passwords, QR codes, and quick network lookups.',
    icon: 'globe',
    accent: 'amber',
    order: 6,
  },
  {
    id: 'files',
    label: 'Files',
    description: 'Convert, merge, and clean up documents in your browser.',
    icon: 'file-text',
    accent: 'slate',
    order: 7,
  },
  {
    id: 'fun',
    label: 'Fun',
    description: 'Random pickers and quick breaks.',
    icon: 'dice-5',
    accent: 'orange',
    order: 8,
  },
] as const satisfies readonly ToolCategoryDef[];

export type ToolCategoryId = (typeof toolCategories)[number]['id'];

export const toolCategoryLabels = Object.fromEntries(
  toolCategories.map((c) => [c.id, c.label]),
) as Record<ToolCategoryId, string>;

export function getToolCategory(id: ToolCategoryId): (typeof toolCategories)[number] {
  const category = toolCategories.find((c) => c.id === id);
  if (!category) throw new Error(`Unknown tool category: ${id}`);
  return category;
}

export function getSortedToolCategories(): (typeof toolCategories)[number][] {
  return [...toolCategories].sort((a, b) => a.order - b.order);
}
